import { useState, useCallback, useMemo } from 'react';
import ChatService from '../services/chatService';

/**
 * Custom hook for managing conversation list state and operations
 * Consolidates filtering, sorting, pagination, and CRUD operations
 */
export const useConversationList = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [selectedConversations, setSelectedConversations] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  
  // Pagination settings
  const CONVERSATIONS_PER_PAGE = 6;

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[useConversationList] Loading conversations...');
      const result = await ChatService.getAllConversations();
      console.log('[useConversationList] getAllConversations result:', {
        success: result.success,
        count: result.data?.length,
        error: result.error,
        data: result.data
      });
      if (result.success) {
        const conversations = result.data || [];
        console.log('[useConversationList] Setting conversations:', conversations.length, 'conversations');
        setConversations(conversations);
      } else {
        console.error('[useConversationList] Failed to load conversations:', result.error);
        setError('Failed to load conversations');
      }
    } catch (err) {
      console.error('[useConversationList] Exception loading conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update conversation status
  const updateConversationStatus = useCallback(async (conversationId, newStatus, userProfileId) => {
    if (!userProfileId) {
      setError('User profile is required');
      return { success: false };
    }

    try {
      setError(null);
      const result = await ChatService.updateConversationStatus(
        conversationId,
        newStatus,
        userProfileId
      );
      
      if (result.success) {
        setConversations(prev =>
          prev.map(c => c.id === conversationId ? { ...c, status: newStatus } : c)
        );
      } else {
        setError('Failed to update conversation status');
      }
      return result;
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.message || 'Failed to update status');
      return { success: false };
    }
  }, []);

  // Bulk update status
  const bulkUpdateStatus = useCallback(async (conversationIds, newStatus, userProfileId) => {
    if (!userProfileId) {
      setError('User profile is required');
      return { success: false };
    }

    try {
      setError(null);
      const results = await Promise.all(
        conversationIds.map(convId =>
          ChatService.updateConversationStatus(convId, newStatus, userProfileId)
        )
      );

      const allSuccessful = results.every(r => r.success);
      if (allSuccessful) {
        setConversations(prev =>
          prev.map(c =>
            conversationIds.includes(c.id) ? { ...c, status: newStatus } : c
          )
        );
        setSelectedConversations(new Set());
      } else {
        setError('Some conversations failed to update');
      }
      return { success: allSuccessful };
    } catch (err) {
      console.error('Failed to bulk update:', err);
      setError(err.message || 'Failed to update conversations');
      return { success: false };
    }
  }, []);

  // Archive conversation
  const archiveConversation = useCallback(async (conversationId, userProfileId) => {
    if (!userProfileId) {
      setError('User profile is required');
      return { success: false };
    }

    try {
      setError(null);
      const result = await ChatService.archiveConversation(conversationId, userProfileId);
      
      if (result.success) {
        setConversations(prev =>
          prev.map(c => 
            c.id === conversationId 
              ? { ...c, is_archived: true, archived_at: new Date().toISOString(), archived_by: userProfileId }
              : c
          )
        );
      } else {
        setError('Failed to archive conversation');
      }
      return result;
    } catch (err) {
      console.error('Failed to archive conversation:', err);
      setError(err.message || 'Failed to archive conversation');
      return { success: false };
    }
  }, []);

  // Bulk archive conversations
  const bulkArchiveConversations = useCallback(async (conversationIds, userProfileId) => {
    if (!userProfileId) {
      setError('User profile is required');
      return { success: false };
    }

    try {
      setError(null);
      const results = await Promise.all(
        conversationIds.map(convId =>
          ChatService.archiveConversation(convId, userProfileId)
        )
      );

      const allSuccessful = results.every(r => r.success);
      if (allSuccessful) {
        const now = new Date().toISOString();
        setConversations(prev =>
          prev.map(c =>
            conversationIds.includes(c.id) 
              ? { ...c, is_archived: true, archived_at: now, archived_by: userProfileId }
              : c
          )
        );
        setSelectedConversations(new Set());
        setCurrentPage(1);
      } else {
        setError('Some conversations failed to archive');
      }
      return { success: allSuccessful };
    } catch (err) {
      console.error('Failed to bulk archive:', err);
      setError(err.message || 'Failed to archive conversations');
      return { success: false };
    }
  }, []);

  // Memoized filtered and sorted conversations
  const filteredConversations = useMemo(() => {
    console.log('[useConversationList] Computing filtered conversations:', {
      totalConversations: conversations.length,
      showArchived,
      filter,
      sortBy,
      searchQuery,
      conversationsPreview: conversations.slice(0, 3).map(c => ({
        id: c.id,
        subject: c.subject,
        status: c.status,
        is_archived: c.is_archived
      }))
    });
    
    let result = conversations;

    // Apply archive filter
    const beforeArchiveFilter = result.length;
    result = result.filter(conv => {
      // Treat NULL as false (unarchived) for backward compatibility with old records
      const isArchived = conv.is_archived ?? false;
      const shouldShow = isArchived === showArchived;
      if (!shouldShow && beforeArchiveFilter <= 3) {
        // Debug: Log first few filtered out conversations
        console.log('[useConversationList] Filtered out conversation:', {
          id: conv.id,
          subject: conv.subject,
          is_archived: conv.is_archived,
          showArchived,
          reason: `is_archived(${isArchived}) !== showArchived(${showArchived})`
        });
      }
      return shouldShow;
    });
    console.log('[useConversationList] After archive filter:', {
      before: beforeArchiveFilter,
      after: result.length,
      showArchived,
      samples: result.slice(0, 2).map(c => ({ id: c.id, archived: c.is_archived }))
    });

    // Filter out resolved/closed conversations unless explicitly showing archived
    // This prevents admins from accidentally responding to finished conversations
    if (!showArchived) {
      const beforeClosedFilter = result.length;
      result = result.filter(conv => 
        conv.status !== 'resolved' && conv.status !== 'closed'
      );
      console.log('[useConversationList] After resolved/closed filter:', { 
        before: beforeClosedFilter, 
        after: result.length 
      });
    }

    // Apply status filter
    if (filter !== 'all') {
      const beforeStatusFilter = result.length;
      result = result.filter(conv => conv.status === filter);
      console.log('[useConversationList] After status filter:', { before: beforeStatusFilter, after: result.length });
    }

    // Apply search with sanitization
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const beforeSearch = result.length;
      result = result.filter(conv =>
        (conv.user?.full_name?.toLowerCase().includes(query)) ||
        (conv.subject?.toLowerCase().includes(query)) ||
        (conv.user?.email?.toLowerCase().includes(query))
      );
      console.log('[useConversationList] After search filter:', { before: beforeSearch, after: result.length });
    }

    // Apply sorting
    result.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      
      if (sortBy === 'priority') {
        return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      } else if (sortBy === 'created') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      // Default: 'updated'
      return new Date(b.updated_at) - new Date(a.updated_at);
    });

    console.log('[useConversationList] Final filtered result:', result.length, 'conversations');
    return result;
  }, [conversations, filter, sortBy, searchQuery, showArchived]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredConversations.length / CONVERSATIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * CONVERSATIONS_PER_PAGE;
  const endIndex = startIndex + CONVERSATIONS_PER_PAGE;
  const paginatedConversations = filteredConversations.slice(startIndex, endIndex);

  return {
    conversations,
    filteredConversations: paginatedConversations,
    allFilteredConversations: filteredConversations,
    loading,
    error,
    filter,
    setFilter,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedConversations,
    setSelectedConversations,
    loadConversations,
    updateConversationStatus,
    bulkUpdateStatus,
    archiveConversation,
    bulkArchiveConversations,
    currentPage,
    setCurrentPage,
    totalPages,
    showArchived,
    setShowArchived
  };
};
